// File: src/components/admin/plans/PlanEditor.js
// per-plan editor card. One card == one plan.
//
// Composes:
//   - Marketing fields (displayName, tagline, featureBullets[], badge, sortOrder)
// Caps (the 5 fields)
//   - Stripe Price picker (with verify + decouple toggle)
//   - Live cost panel (right rail on lg+)
//   - Live customer preview card
//   - Save → diff modal → confirm
//   - Archive / Unarchive / Duplicate actions
//   - History accordion

import React, { useEffect, useState, useMemo } from 'react';
import {
    Card, CardContent, CardActions, Grid, Stack, TextField, Button,
    Typography, Checkbox, FormControlLabel, MenuItem, Chip, Accordion, AccordionSummary,
    AccordionDetails, CircularProgress, Alert, Tabs, Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/EditOutlined';
import DataObjectIcon from '@mui/icons-material/DataObject';

import StripePricePicker from './StripePricePicker';
import PlanCostPanel from './PlanCostPanel';
import PlanPreviewCard from './PlanPreviewCard';
import PlanDiffDialog from './PlanDiffDialog';
import PlanHistoryList from './PlanHistoryList';
import PlanMarketingFields from './PlanMarketingFields';
import JsonViewTab from '../common/JsonViewTab';
import { mainApi } from '../../../services/mainApi';

const BADGE_OPTIONS = [
    { value: '', label: 'No badge' },
    { value: 'POPULAR', label: 'Most popular' },
    { value: 'BEST_VALUE', label: 'Best value' },
    { value: 'NEW', label: 'New' },
];

const INTERVAL_OPTIONS = ['month', 'year'];
const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP'];

function normaliseDraft(plan) {
    return {
        id: plan.id,
        name: plan.name || '',
        displayName: plan.displayName || plan.name || '',
        tagline: plan.tagline || '',
        featureBullets: Array.isArray(plan.featureBullets) ? plan.featureBullets : [],
        badge: plan.badge || '',
        sortOrder: plan.sortOrder ?? 0,
        archived: !!plan.archived,
        // i18n: per-locale overrides for displayName/tagline/bullets.
        // Shape: { fr: {displayName, tagline, featureBullets}, de: {...} }
        i18n: plan.i18n && typeof plan.i18n === 'object' ? plan.i18n : {},
        price: Number(plan.price ?? 0),
        currency: plan.currency || 'EUR',
        interval: plan.interval || 'month',
        stripePriceId: plan.stripePriceId || '',
        stripeProductId: plan.stripeProductId || '',
        monthlyAiActions: Number(plan.monthlyAiActions ?? 0),
        activeCampaigns: Number(plan.activeCampaigns ?? 0),
        storageMb: Number(plan.storageMb ?? 0),
        apiEnabled: !!plan.apiEnabled,
        apiMonthlyRequests: Number(plan.apiMonthlyRequests ?? 0),
    };
}

export default function PlanEditor({ plan, onPlanChanged }) {
    const [draft, setDraft] = useState(() => normaliseDraft(plan));
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [decoupled, setDecoupled] = useState(false);
    const [diffOpen, setDiffOpen] = useState(false);
    const [userCount, setUserCount] = useState(null);
    const [historyRefresh, setHistoryRefresh] = useState(0);
    // i18n: active locale tab also drives the preview card so the
    // admin sees a WYSIWYG preview of the current locale's copy.
    const [activeLocale, setActiveLocale] = useState('en');
    // Top-level tab: 0 = Form, 1 = JSON. JSON tab edits the same draft state
    // so switching back to Form picks up the parsed/applied changes.
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        setDraft(normaliseDraft(plan));
        setDirty(false);
        setError(null);
    }, [plan]);

    function patch(updates) {
        setDraft(prev => ({ ...prev, ...updates }));
        setDirty(true);
    }

    async function handleSaveClick() {
        // Look up user count before showing the diff modal so the callout
        // is accurate. Failure here is non-blocking (modal shows "unknown").
        try {
            const res = await mainApi.countUsersOnPlan(plan.id);
            setUserCount(res?.count ?? -1);
        } catch {
            setUserCount(-1);
        }
        setDiffOpen(true);
    }

    async function handleConfirmSave() {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const updated = await mainApi.updatePlan(plan.id, draft);
            setDiffOpen(false);
            setSaving(false);
            setSuccess('Live for all users on this plan.');
            setDirty(false);
            setHistoryRefresh(x => x + 1);
            if (onPlanChanged) onPlanChanged(updated);
        } catch (e) {
            const code = e?.response?.data?.code;
            const msg = e?.response?.data?.error || e.message;
            setError(code ? `${code}: ${msg}` : msg);
            setSaving(false);
        }
    }

    async function handleArchive() {
        try {
            const updated = draft.archived
                ? await mainApi.unarchivePlan(plan.id)
                : await mainApi.archivePlan(plan.id);
            setSuccess(updated.archived ? 'Plan archived.' : 'Plan unarchived.');
            setHistoryRefresh(x => x + 1);
            if (onPlanChanged) onPlanChanged(updated);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
        }
    }

    async function handleDuplicate() {
        try {
            const dup = await mainApi.duplicatePlan(plan.id);
            setSuccess(`Created ${dup.id} (archived).`);
            if (onPlanChanged) onPlanChanged(dup, { wasCreated: true });
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
        }
    }

    // JSON tab → "Apply Changes" merges parsed JSON into the draft. We don't
    // POST anything; the existing Save button still performs the PUT (with
    // the diff modal) so the operator can audit what they're about to push.
    // Field allowlist mirrors the BE PLAN_HSET_ALLOWED so unknown keys are
    // dropped silently instead of being sent to the server.
    const JSON_ALLOWED_FIELDS = useMemo(() => new Set([
        'id', 'name', 'displayName', 'tagline', 'featureBullets', 'badge',
        'sortOrder', 'archived', 'i18n',
        'price', 'currency', 'interval', 'stripePriceId', 'stripeProductId',
        'monthlyAiActions', 'activeCampaigns', 'storageMb', 'apiEnabled',
        'apiMonthlyRequests',
    ]), []);

    function handleJsonApply(parsed) {
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            setError('JSON must be an object');
            return;
        }
        // id is informational here — the URL drives which plan we PUT to.
        // Reject id changes loudly so the operator doesn't accidentally
        // try to rename a plan via the JSON tab.
        if (parsed.id != null && parsed.id !== plan.id) {
            setError(`JSON "id" (${parsed.id}) does not match this plan (${plan.id}). Refusing to apply.`);
            return;
        }
        const filtered = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (JSON_ALLOWED_FIELDS.has(k)) filtered[k] = v;
        }
        patch(filtered);
        setError(null);
    }

    const showStripeWarning = useMemo(() => {
        if (!plan.stripePriceId) return false;
        if (!draft.price || !plan.price) return false;
        return decoupled && draft.price !== plan.price;
    }, [decoupled, draft.price, plan.price, plan.stripePriceId]);

    return (
        <Card
            variant="outlined"
            data-testid={`plan-editor-${plan.id}`}
            sx={{ opacity: plan.archived ? 0.85 : 1 }}
        >
            <CardContent>
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
                        {plan.displayName || plan.name} <Typography component="span" color="text.secondary">({plan.id})</Typography>
                    </Typography>
                    {plan.archived && (
                        <Chip
                            size="small"
                            color="warning"
                            label="Archived"
                            data-testid={`plan-archived-chip-${plan.id}`}
                        />
                    )}
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}
                        data-testid={`plan-error-${plan.id}`}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}
                        data-testid={`plan-success-${plan.id}`}>
                        {success}
                    </Alert>
                )}

                <Tabs
                    value={activeTab}
                    onChange={(_e, v) => setActiveTab(v)}
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                    data-testid={`plan-tabs-${plan.id}`}
                >
                    <Tab
                        label="Form" icon={<EditIcon fontSize="small" />} iconPosition="start"
                        sx={{ minHeight: 40, textTransform: 'none' }}
                        data-testid={`plan-tab-form-${plan.id}`}
                    />
                    <Tab
                        label="JSON" icon={<DataObjectIcon fontSize="small" />} iconPosition="start"
                        sx={{ minHeight: 40, textTransform: 'none' }}
                        data-testid={`plan-tab-json-${plan.id}`}
                    />
                </Tabs>

                {activeTab === 1 && (
                    <Card variant="outlined" data-testid={`plan-json-card-${plan.id}`}>
                        <JsonViewTab data={draft} onSave={handleJsonApply} />
                    </Card>
                )}

                {activeTab === 0 && (
                <Grid container spacing={3}>
                    {/* Left column — fields */}
                    <Grid item xs={12} lg={8}>
                        <Stack spacing={2}>
                            {/* Marketing */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Marketing copy
                            </Typography>
                            <PlanMarketingFields
                                plan={plan} draft={draft} patch={patch}
                                activeLocale={activeLocale} onLocaleChange={setActiveLocale}
                            />

                            {/* Operational marketing fields — not translated */}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                                <TextField
                                    label="Internal name"
                                    size="small"
                                    fullWidth
                                    value={draft.name}
                                    onChange={(e) => patch({ name: e.target.value })}
                                    inputProps={{ 'data-testid': `plan-name-${plan.id}` }}
                                />
                                <TextField
                                    select
                                    label="Badge"
                                    size="small"
                                    sx={{ minWidth: 180 }}
                                    value={draft.badge}
                                    onChange={(e) => patch({ badge: e.target.value })}
                                    inputProps={{ 'data-testid': `plan-badge-${plan.id}` }}
                                >
                                    {BADGE_OPTIONS.map(o => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Sort order"
                                    type="number"
                                    size="small"
                                    sx={{ width: 140 }}
                                    value={draft.sortOrder}
                                    onChange={(e) => patch({ sortOrder: Number(e.target.value) })}
                                    inputProps={{ 'data-testid': `plan-sortOrder-${plan.id}` }}
                                />
                            </Stack>

                            {/* Pricing */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', pt: 1 }}>
                                Pricing
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="Price (cents)"
                                    type="number"
                                    size="small"
                                    value={draft.price}
                                    onChange={(e) => patch({ price: Number(e.target.value) })}
                                    sx={{ width: 160 }}
                                    inputProps={{ 'data-testid': `plan-price-${plan.id}`, min: 0 }}
                                />
                                <TextField
                                    select
                                    label="Currency"
                                    size="small"
                                    value={draft.currency}
                                    onChange={(e) => patch({ currency: e.target.value })}
                                    sx={{ width: 120 }}
                                    inputProps={{ 'data-testid': `plan-currency-${plan.id}` }}
                                >
                                    {CURRENCY_OPTIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </TextField>
                                <TextField
                                    select
                                    label="Interval"
                                    size="small"
                                    value={draft.interval}
                                    onChange={(e) => patch({ interval: e.target.value })}
                                    sx={{ width: 120 }}
                                    inputProps={{ 'data-testid': `plan-interval-${plan.id}` }}
                                >
                                    {INTERVAL_OPTIONS.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                                </TextField>
                            </Stack>

                            <StripePricePicker
                                planId={plan.id}
                                value={draft.stripePriceId}
                                currency={draft.currency}
                                expectedAmount={draft.price}
                                decoupled={decoupled}
                                onDecouple={setDecoupled}
                                onChange={(v) => patch({ stripePriceId: v })}
                                onPickedAutoFill={(autofill) => patch(autofill)}
                            />

                            <TextField
                                label="Stripe Product ID"
                                size="small"
                                fullWidth
                                value={draft.stripeProductId}
                                onChange={(e) => patch({ stripeProductId: e.target.value })}
                                inputProps={{ 'data-testid': `plan-stripeProductId-${plan.id}` }}
                                helperText="Auto-filled when you pick a Price above."
                            />

                            {showStripeWarning && (
                                <Alert severity="warning" data-testid={`plan-stripe-decouple-warning-${plan.id}`}>
                                    Display price differs from the Stripe Price amount. Stripe will charge the Price amount, not what you typed here.
                                </Alert>
                            )}

                            {/* Caps */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', pt: 1 }}>
                                Quota caps
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="Monthly AI actions"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={draft.monthlyAiActions}
                                    onChange={(e) => patch({ monthlyAiActions: Number(e.target.value) })}
                                    helperText="0 = unlimited"
                                    inputProps={{ 'data-testid': `plan-monthlyAiActions-${plan.id}`, min: 0 }}
                                />
                                <TextField
                                    label="Active campaigns"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={draft.activeCampaigns}
                                    onChange={(e) => patch({ activeCampaigns: Number(e.target.value) })}
                                    helperText="0 = unlimited"
                                    inputProps={{ 'data-testid': `plan-activeCampaigns-${plan.id}`, min: 0 }}
                                />
                                <TextField
                                    label="Storage (MB)"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={draft.storageMb}
                                    onChange={(e) => patch({ storageMb: Number(e.target.value) })}
                                    inputProps={{ 'data-testid': `plan-storageMb-${plan.id}`, min: 0 }}
                                />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                <FormControlLabel
                                    control={<Checkbox
                                        checked={draft.apiEnabled}
                                        onChange={(e) => patch({ apiEnabled: e.target.checked })}
                                        inputProps={{ 'data-testid': `plan-apiEnabled-${plan.id}` }}
                                    />}
                                    label="API access enabled"
                                />
                                <TextField
                                    label="API requests / month"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={draft.apiMonthlyRequests}
                                    onChange={(e) => patch({ apiMonthlyRequests: Number(e.target.value) })}
                                    disabled={!draft.apiEnabled}
                                    inputProps={{ 'data-testid': `plan-apiMonthlyRequests-${plan.id}`, min: 0 }}
                                />
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Right column — cost + preview */}
                    <Grid item xs={12} lg={4}>
                        <Stack spacing={2} sx={{ position: { lg: 'sticky' }, top: 16 }}>
                            <PlanCostPanel planId={plan.id} draft={draft} />
                            <PlanPreviewCard planId={plan.id} draft={draft} locale={activeLocale} />
                        </Stack>
                    </Grid>
                </Grid>
                )}

                {/* History — visible regardless of tab; it's a read-only reference. */}
                <Accordion sx={{ mt: 3 }} data-testid={`plan-history-accordion-${plan.id}`}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>Change history</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <PlanHistoryList planId={plan.id} refreshKey={historyRefresh} />
                    </AccordionDetails>
                </Accordion>
            </CardContent>

            <CardActions sx={{ p: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        startIcon={plan.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                        onClick={handleArchive}
                        color={plan.archived ? 'success' : 'warning'}
                        data-testid={`plan-archive-btn-${plan.id}`}
                    >
                        {plan.archived ? 'Unarchive' : 'Archive'}
                    </Button>
                    <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleDuplicate}
                        data-testid={`plan-duplicate-btn-${plan.id}`}
                    >
                        Duplicate
                    </Button>
                </Stack>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveClick}
                    disabled={!dirty || saving}
                    data-testid={`save-plan-${plan.id}`}
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </CardActions>

            <PlanDiffDialog
                open={diffOpen}
                planId={plan.id}
                planLabel={plan.displayName || plan.name}
                before={normaliseDraft(plan)}
                after={draft}
                userCount={userCount}
                saving={saving}
                onCancel={() => setDiffOpen(false)}
                onConfirm={handleConfirmSave}
            />
        </Card>
    );
}
